import { type Action } from 'redux';
import {
  buffers,
  type Channel,
} from 'redux-saga';
import {
  actionChannel, call, delay, put,
  race, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import type { ArrayElement } from '~/_shared/utils/array/array.helpers';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { boundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { type BoundaryItemsAction } from '~/store/boundaryItems/boundaryItems.action';
import { BOUNDARY_ITEMS_UPDATE_ITEMS } from '~/store/boundaryItems/boundaryItems.actionTypes';
import { waitForBoundaryGroups } from '~/store/boundaryItems/boundaryItems.sagas';
import { watchForLoadingOfZoomedInBoundaryDetails } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.watcher.saga';
import { boundaryTerritoryGroupUpdateSuccess } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  activeBoundaryTerritoryGroupsSelector, boundaryTerritoryGroupsSelector, findBoundaryTerritoryGroupSelector, type GetBTGById,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { MAP_SETTINGS_FETCH_DATA_SUCCESS } from '~/store/mapSettings/data/mapSettingsData.actionTypes';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { isMapInOfflineModeSelector } from '~/store/selectors/useMapInfoSelectors';
import { createCancelToken } from '../../_shared/utils/api/api.helpers';
import {
  createUuid, type Uuid,
} from '../../_shared/utils/createUuid';
import { select } from '../../_shared/utils/saga/effects';
import { type PickAction } from '../../_shared/utils/types/action.type';
import { isBoundaryTerritoryEmpty } from '../../sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import { type BoundaryTerritoryGroupId } from '../boundaries/boundaryIdentifier.type';
import { type BoundaryTerritoryGroupsAction } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import {
  BOUNDARY_TERRITORY_CHANGE_NAME,
  BOUNDARY_TERRITORY_DELETE,
  BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS,
  BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
  BOUNDARY_TERRITORY_GROUP_UPDATE_ASSIGNMENTS_SUCCESS,
  BOUNDARY_TERRITORY_GROUPS_CREATE_CUSTOM_TERRITORY,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import { createBoundaryTerritoryUpdateRequestFromBoundaryTerritoryGroup } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import {
  type BoundaryTerritoryGroupUpdateRequest, updateBoundaryTerritoryGroup,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import { type BoundaryTerritoryGroup } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { primarySpreadsheetIdSelector } from '../mapInfo/mapInfo.selectors';
import { waitForMapSettingsReady } from '../mapSettings/ready/mapSettingsReady.sagas.helpers';
import { type BoundaryFilters } from '../mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.selectors';
import { boundaryFiltersSelector } from '../mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.selectors';
import { type FilterTreeItemRequest } from '../spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { getStoreFilterTree } from '../spreadsheetData/filterTree/spreadsheetDataFilterTree.helpers';
import {
  boundaryTerritoryDetailsInvalidate,
  boundaryTerritoryDetailsUpdateManualAssignments,
  boundaryTerritoryGroupsFetchDetailsError,
  boundaryTerritoryGroupsFetchDetailsLoading,
  boundaryTerritoryGroupsFetchDetailsRequest,
  boundaryTerritoryGroupsFetchDetailsSuccess,
} from './boundaryTerritoryDetails.actionCreators';
import { getBtgExtraDataQuery } from './boundaryTerritoryDetails.helpers';
import {
  type BoundaryTerritoryGroupDetailsRequestQueryModel,
  getBoundaryTerritoryGroupDetails,
} from './boundaryTerritoryDetails.repository';
import {
  type BoundaryTerritoryAssignment,
  boundaryTerritoryAssignmentsSelector,
} from './boundaryTerritoryDetails.selectors';
import {
  type BoundaryTerritoryGroupDetails, type BoundaryTerritoryGroupDetailsItem,
} from './boundaryTerritoryDetails.types';

export function* boundaryTerritoryDetailsSagas() {
  yield takeLatest([
    BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
  ], requestBoundaryTerritoryGroupsDetails);
  yield takeEvery(boundaryTerritoryGroupsFetchDetailsRequest, getBoundaryTerritoryGroupsDetails);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS, onBoundaryTerritoryGroupCreate);
  yield takeEvery(boundaryTerritoryGroupUpdateSuccess, onBoundaryTerritoryGroupUpdate);
  yield takeEvery(BOUNDARY_ITEMS_UPDATE_ITEMS, onBoundaryItemsUpdate);
  yield takeLatest([
    BOUNDARY_TERRITORY_GROUPS_CREATE_CUSTOM_TERRITORY,
    BOUNDARY_TERRITORY_CHANGE_NAME,
    BOUNDARY_TERRITORY_DELETE,
  ], onAddCustomBoundaryTerritory);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_UPDATE_ASSIGNMENTS_SUCCESS,
    onBoundaryTerritoryGroupUpdateAssignmentsSuccess);
  yield takeLatest(boundaryTerritoryGroupsFetchDetailsSuccess, verifyFilteredBoundaryTerritories);
  yield takeLatest(MAP_SETTINGS_FETCH_DATA_SUCCESS, watchMapSettingsFilteringChanges);
  yield takeLatest(BOUNDARY_TERRITORY_DELETE, onBoundaryTerritoryDelete);
  yield takeLatest(MAP_SETTINGS_FETCH_DATA_SUCCESS, watchForLoadingOfZoomedInBoundaryDetails);
}

function* verifyFilteredBoundaryTerritories() {
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> =
    yield select(boundaryTerritoryGroupsSelector);

  const boundaryTerritoryAssignments: ReadonlyMap<BoundaryTerritoryGroupId, BoundaryTerritoryAssignment> =
    yield select<ReadonlyMap<BoundaryTerritoryGroupId, BoundaryTerritoryAssignment>>(
      boundaryTerritoryAssignmentsSelector,
    );

  const filters: BoundaryFilters = yield select<BoundaryFilters>(boundaryFiltersSelector);

  for (const group of boundaryTerritoryGroups) {
    const emptyBoundaryTerritories: string[] = [];
    for (const territory of group.settings.boundaryTerritories) {
      if (isBoundaryTerritoryEmpty(boundaryTerritoryAssignments, group.boundaryTerritoryGroupId, territory)) {
        emptyBoundaryTerritories.push(territory.boundaryTerritoryId);
      }
    }

    const filter = filters.get(group.boundaryGroupId);

    // empty boundary territories cannot be filtered
    const newFilteredBoundaryTerritories = filter.filteredBoundaryTerritories
      .filter(id => !emptyBoundaryTerritories.includes(id));

    if (newFilteredBoundaryTerritories.length < filter.filteredBoundaryTerritories.length) {
      yield put(setBoundaryFilter(group.boundaryGroupId, {
        ...filter,
        filteredBoundaryTerritories: newFilteredBoundaryTerritories,
        showAll: newFilteredBoundaryTerritories.length === 0 ? TriStateRange.Full : filter.showAll,
      }));
    }
  }
}

function* watchMapSettingsFilteringChanges() {
  let isMapFirstLoad = true;
  let currentFilterHash = '';

  // Buffer latest map settings action when the saga is busy and not taking actions.
  const mapSettingsChannel: Channel<Action> = yield actionChannel(
    (action: Action) => action.type.startsWith('MAP_SETTINGS'), buffers.sliding(1));

  while (true) {
    yield call(waitForMapSettingsReady);

    if (isMapFirstLoad) {
      isMapFirstLoad = false;
      const initialFilterTrees: FilterTreeItemRequest | null = yield getStoreFilterTree();
      currentFilterHash = initialFilterTrees?.filterHash ?? '';
      yield requestBoundaryTerritoryGroupsDetails();
    }

    yield take(mapSettingsChannel);

    while (true) {
      const { timeout } = yield race({
        timeout: delay(1000),
        latestAction: take(mapSettingsChannel),
      });

      if (timeout) {
        break;
      }
    }

    const combinedFilterTrees: FilterTreeItemRequest | null = yield getStoreFilterTree();
    const newFilterHash = combinedFilterTrees?.filterHash ?? '';

    if (currentFilterHash === newFilterHash) {
      continue;
    }

    currentFilterHash = newFilterHash;
    yield put(boundaryTerritoryDetailsInvalidate());
    yield requestBoundaryTerritoryGroupsDetails();
  }
}

function* onBoundaryTerritoryGroupCreate(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS>,
) {
  yield put(boundaryTerritoryGroupsFetchDetailsRequest(action.payload.boundaryTerritoryGroups));
}

// update details for a boundary territory group if an update took place
function* onBoundaryTerritoryGroupUpdate(
  action: ReturnType<typeof boundaryTerritoryGroupUpdateSuccess>,
) {
  if (action.payload.fetchBoundaryDetails) {
    yield put(boundaryTerritoryGroupsFetchDetailsRequest(action.payload.boundaryTerritoryGroups));
  }
}

// update details of all affected boundary territory groups if a boundary group item has been updated
function* onBoundaryItemsUpdate(action: PickAction<BoundaryItemsAction, typeof BOUNDARY_ITEMS_UPDATE_ITEMS>) {
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);
  const boundaryTerritoryGroupsToFetch: ReadonlyArray<BoundaryTerritoryGroup> = boundaryTerritoryGroups.filter(btg => btg.boundaryGroupId === action.payload.boundaryGroupId);

  yield put(boundaryTerritoryGroupsFetchDetailsRequest(boundaryTerritoryGroupsToFetch));
}

function* requestBoundaryTerritoryGroupsDetails() {
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);
  const isMapSettingsReady: boolean = yield select<boolean>(state => state.map.mapSettings.isReady);

  if (boundaryTerritoryGroups.length && isMapSettingsReady) {
    yield put(boundaryTerritoryGroupsFetchDetailsRequest(boundaryTerritoryGroups));
  }
}

const filterBtgsThatRequireNoZoom = (
  btgs: readonly BoundaryTerritoryGroup[],
  boundaryGroups: readonly BoundaryGroup[],
) => {
  return btgs.filter(btg => {
    const bg = boundaryGroups.find(group => group.id === btg.boundaryGroupId);
    if (bg) {
      return bg.zoomLevels.includes(0);
    }
    return false;
  });
};

function* getBoundaryTerritoryGroupsDetails(
  action: ReturnType<typeof boundaryTerritoryGroupsFetchDetailsRequest>
) {
  // Buffer any invalidate dispatched during preparatory work so the race catches it immediately.
  const invalidateChannel: Channel<Action> = yield actionChannel(
    boundaryTerritoryDetailsInvalidate,
    buffers.sliding(1)
  );

  yield call(waitForBoundaryGroups);

  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const spreadsheetId: number | null = yield select<number | null>(primarySpreadsheetIdSelector);

  if (clientId === null || spreadsheetId === null) {
    return;
  }

  const boundaryGroups: ReadonlyArray<BoundaryGroup> = yield select(boundaryGroupsSelector);
  const btgsThatRequireNoZoom = filterBtgsThatRequireNoZoom(action.payload.boundaryTerritoryGroups, boundaryGroups);

  if (btgsThatRequireNoZoom.length === 0) {
    yield put(boundaryTerritoryGroupsFetchDetailsSuccess([]));
    return;
  }

  // Always generate a fresh queryId per fetch so concurrent fetches (e.g. triggered by a filter change)
  // each get a unique id — stale responses are then rejected by the reducer's queryId check.
  const btgQueryIdPairs = btgsThatRequireNoZoom.map(btg => ({
    btg,
    queryId: createUuid(),
  }));

  const btgIds = btgsThatRequireNoZoom.map(btg => btg.boundaryTerritoryGroupId);

  const queryIds: Record<BoundaryTerritoryGroupId, string> = {};
  for (const { btg, queryId } of btgQueryIdPairs) {
    queryIds[btg.boundaryTerritoryGroupId] = queryId;
  }
  yield put(boundaryTerritoryGroupsFetchDetailsLoading(btgIds, queryIds));

  try {
    const BTGsWithQueryId = new Map<Uuid, BoundaryTerritoryGroup>(
      btgQueryIdPairs.map(({ btg, queryId }) => [queryId, btg])
    );

    let combinedFilterTrees: FilterTreeItemRequest | null = null;
    combinedFilterTrees = yield getStoreFilterTree();

    const activeBTGs: ReadonlyArray<BoundaryTerritoryGroup> =
      yield select<ReadonlyArray<BoundaryTerritoryGroup>>(activeBoundaryTerritoryGroupsSelector);
    const activeBTGIds = new Set(activeBTGs.map(btg => btg.boundaryTerritoryGroupId));

    const activeRequests: BoundaryTerritoryGroupDetailsRequestQueryModel[] = [];
    const inactiveRequests: BoundaryTerritoryGroupDetailsRequestQueryModel[] = [];
    BTGsWithQueryId.forEach((btg, queryId) => {
      const request: BoundaryTerritoryGroupDetailsRequestQueryModel = {
        boundaryTerritoryGroup: {
          ...btg,
        },
        boundaryTerritoryGroupQueryId: queryId,
        filter: btg.settings.ignoreFilters || !combinedFilterTrees?.filterTree ? null : {
          filter_tree: combinedFilterTrees.filterTree,
        },
        boundingBox: null,
        extraData: getBtgExtraDataQuery(btg, spreadsheetId),
      };

      if (activeBTGIds.has(btg.boundaryTerritoryGroupId)) {
        activeRequests.push(request);
      }
      else {
        inactiveRequests.push(request);
      }
    });

    // --- Active batch ---
    if (activeRequests.length > 0) {
      const activeCancelToken = createCancelToken();
      const { invalidated: activeInvalidated, response: activeResponse }: { invalidated: Action; response: Map<Uuid, BoundaryTerritoryGroupDetails> } = yield race({
        response: call(getBoundaryTerritoryGroupDetails, clientId, activeRequests, activeCancelToken.token),
        invalidated: take(invalidateChannel),
      });

      if (activeInvalidated) {
        activeCancelToken.cancel();
        return;
      }

      const activeResults: Array<ArrayElement<Parameters<typeof boundaryTerritoryGroupsFetchDetailsSuccess>[0]>> = [];
      for (const [queryId, details] of activeResponse.entries()) {
        const btg = BTGsWithQueryId.get(queryId);
        if (btg) {
          activeResults.push({ boundaryTerritoryGroupId: btg.boundaryTerritoryGroupId, details, queryId });
        }
      }
      yield put(boundaryTerritoryGroupsFetchDetailsSuccess(activeResults));
    }

    // --- Inactive batch (fetched after active is fully processed) ---
    if (inactiveRequests.length > 0) {
      const inactiveCancelToken = createCancelToken();
      const { invalidated: inactiveInvalidated, response: inactiveResponse }: { invalidated: Action; response: Map<Uuid, BoundaryTerritoryGroupDetails> } = yield race({
        response: call(getBoundaryTerritoryGroupDetails, clientId, inactiveRequests, inactiveCancelToken.token),
        invalidated: take(invalidateChannel),
      });

      if (inactiveInvalidated) {
        inactiveCancelToken.cancel();
        return;
      }

      const inactiveResults: Array<ArrayElement<Parameters<typeof boundaryTerritoryGroupsFetchDetailsSuccess>[0]>> = [];
      for (const [queryId, details] of inactiveResponse.entries()) {
        const btg = BTGsWithQueryId.get(queryId);
        if (btg) {
          inactiveResults.push({ boundaryTerritoryGroupId: btg.boundaryTerritoryGroupId, details, queryId });
        }
      }
      yield put(boundaryTerritoryGroupsFetchDetailsSuccess(inactiveResults));
    }
  }
  catch (e) {
    yield put(boundaryTerritoryGroupsFetchDetailsError(e, btgIds));
  }
  finally {
    invalidateChannel.close();
  }
}

function* onBoundaryTerritoryDelete(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_DELETE>
) {
  const getBoundaryTerritoryGroupById: GetBTGById = yield select<GetBTGById>(findBoundaryTerritoryGroupSelector);

  const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(action.payload.boundaryTerritoryGroupId);

  if (!boundaryTerritoryGroup) {
    return;
  }

  yield put(boundaryTerritoryGroupsFetchDetailsRequest([boundaryTerritoryGroup]));
}

function* onBoundaryTerritoryGroupUpdateAssignmentsSuccess(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_UPDATE_ASSIGNMENTS_SUCCESS>
) {
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);

  if (!isMapOffline) {
    const boundaryTerritoryGroup: BoundaryTerritoryGroup | undefined = boundaryTerritoryGroups
      .find(group => group.boundaryTerritoryGroupId === action.payload.boundaryTerritoryGroupId);

    if (boundaryTerritoryGroup) {
      yield put(boundaryTerritoryGroupsFetchDetailsRequest([boundaryTerritoryGroup]));
    }
  }
  else {
    const boundaryTerritoriesDetails: ReadonlyMap<BoundaryTerritoryGroupId, BoundaryTerritoryGroupDetailsItem> =
      yield select<ReadonlyMap<number, BoundaryTerritoryGroupDetailsItem>>(
        state => state.map.boundaryTerritoryDetails.territoryDetails.data
      );

    const boundaryTerritoryDetails: BoundaryTerritoryGroupDetailsItem | undefined = boundaryTerritoriesDetails.get(
      action.payload.boundaryTerritoryGroupId);
    if (!boundaryTerritoryDetails) {
      return;
    }

    const newManualAssignments: {
      [boundaryTerritoryId: string]: readonly number[];
    } = { ...boundaryTerritoryDetails.manualAssignments };

    for (const request of action.payload.requests) {
      const setBoundaryIds: number[] = request.set?.map(item => item.boundary_id) ?? [];
      const removeBoundaryIds: number[] = request.remove ?? [];
      let newTerritoryAssignments: readonly number[] = newManualAssignments[request.boundary_territory_id] ?? [];
      newTerritoryAssignments = newTerritoryAssignments.filter(boundaryId => !removeBoundaryIds.includes(boundaryId) && !setBoundaryIds.includes(boundaryId));
      newTerritoryAssignments = newTerritoryAssignments.concat(setBoundaryIds);
      newManualAssignments[request.boundary_territory_id] = newTerritoryAssignments;
    }

    yield put(boundaryTerritoryDetailsUpdateManualAssignments(action.payload.boundaryTerritoryGroupId, newManualAssignments));
  }
}

function* onAddCustomBoundaryTerritory(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUPS_CREATE_CUSTOM_TERRITORY>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const updatedBoundaryTerritoryGroupId: number = action.payload.boundaryTerritoryGroupId;
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);
  const updatedState = boundaryTerritoryGroups.find(group => group.boundaryTerritoryGroupId === updatedBoundaryTerritoryGroupId);

  if (!updatedState || !clientId) {
    return;
  }
  const payload: BoundaryTerritoryGroupUpdateRequest = createBoundaryTerritoryUpdateRequestFromBoundaryTerritoryGroup(
    updatedState,
  );

  const newTerritoryGroup: BoundaryTerritoryGroup = yield call(updateBoundaryTerritoryGroup, clientId, payload);
  yield put(boundaryTerritoryGroupUpdateSuccess([newTerritoryGroup], { fetchBoundaryDetails: false }));

  action.payload.onSuccess?.(action.payload.newTerritoryId);
}
